// Незавършените броения — чистата логика, обща за началния екран на
// „Инвентаризация" и таблото: кой склад брои черновата и как се казва на един ред
// („Бар · Мария · 12 преброени · вчера 18:40", заета — „в момента брои Мария").
//
// Свързването чернова → склад е по `warehouseId` (BE с полето, локалната
// чернова), а до BE без него — по етикета: името на склада КЪМ момента на записа
// (преименуван склад тогава не се хваща).
import dayjs, { type Dayjs } from "dayjs";
import type { TFunction } from "i18next";
import type { DraftSummary } from "../../../components/drafts/draftModel";

type WarehouseRef = { id: number; name: string };

const normalized = (text: string | null | undefined) => (text ?? "").trim().toLocaleLowerCase("bg");

// Складът, който черновата брои; null — не се разпознава
export const draftWarehouseIdOf = (draft: DraftSummary, warehouses: WarehouseRef[]): number | null => {
  if (draft.warehouseId != null) return draft.warehouseId;
  const label = normalized(draft.label);
  if (!label) return null;
  return warehouses.find((warehouse) => normalized(warehouse.name) === label)?.id ?? null;
};

// склад → незавършените му (моята първа, после на колегите по реда от сървъра)
export const unfinishedByWarehouse = (
  mine: DraftSummary | null,
  others: DraftSummary[],
  warehouses: WarehouseRef[],
): Map<number, DraftSummary[]> => {
  const byWarehouse = new Map<number, DraftSummary[]>();
  for (const draft of [...(mine ? [mine] : []), ...others]) {
    const warehouseId = draftWarehouseIdOf(draft, warehouses);
    if (warehouseId == null) continue;
    byWarehouse.set(warehouseId, [...(byWarehouse.get(warehouseId) ?? []), draft]);
  }
  return byWarehouse;
};

// Незавършена моя ли е картата (локалната или моята от сървъра)
export const isMineDraft = (draft: DraftSummary, mine: DraftSummary | null) =>
  mine != null && draft.key === mine.key;

// „днес 18:40" / „вчера 18:40" / „28.09 18:40" — по календарен ден
export const draftWhenLabel = (t: TFunction, iso: string, now: Dayjs = dayjs()) => {
  const moment = dayjs(iso);
  if (!iso || !moment.isValid()) return null;
  const time = moment.format("HH:mm");
  const days = now.startOf("day").diff(moment.startOf("day"), "day");
  if (days <= 0) return t("inventoryPage.stkWhenToday", { time });
  if (days === 1) return t("inventoryPage.stkWhenYesterday", { time });
  return moment.format("DD.MM HH:mm");
};

// Кой брои: „вашата" · „в момента брои Мария" (някой я държи отворена) · „Мария"
export const unfinishedWhoLabel = (t: TFunction, draft: DraftSummary, mine: boolean) => {
  if (draft.status === "busy") {
    return t("inventoryPage.stkCountingNow", { name: draft.lockedByName || draft.ownerName || "?" });
  }
  return mine ? t("inventoryPage.stkUnfinishedYours") : draft.ownerName || "—";
};

// Редът на една незавършена: [склад ·] кой · колко преброени [· кога]. При
// „в момента брои" часът не се казва — броенето тече сега.
export const unfinishedStocktakeLine = (
  t: TFunction,
  draft: DraftSummary,
  options: { mine: boolean; withWarehouse: boolean; now?: Dayjs },
) => {
  const busy = draft.status === "busy";
  return [
    options.withWarehouse ? draft.label || t("inventoryPage.dr6NoWarehouse") : null,
    unfinishedWhoLabel(t, draft, options.mine),
    t("inventoryPage.stocktakeCountedShort", { count: draft.linesCount }),
    busy ? null : draftWhenLabel(t, draft.updatedAt, options.now),
  ]
    .filter(Boolean)
    .join(" · ");
};
